import {
  CalendarContextProps,
  CalendarRangeContextProps,
} from "./CalendarContext";
import { DateTime } from "luxon";

export const isSameDay = (d1?: DateTime | null, d2?: DateTime | null) => {
  if (!d1 || !d2) return undefined;
  try {
    if (
      d1.hasSame(d2, "month") &&
      d1.hasSame(d2, "year") &&
      d1.hasSame(d2, "day")
    ) {
      return true;
    }
    return false;
  } catch {
    return false;
  }
};

export const isSameMonth = (d1?: DateTime | null, d2?: DateTime | null) => {
  if (!d1 || !d2) return undefined;
  if (d1.hasSame(d2, "month") && d1.hasSame(d2, "year")) return true;
  return false;
};

export const isSameYear = (d1?: DateTime | null, d2?: DateTime | null) => {
  if (!d1 || !d2) return undefined;
  if (d1.hasSame(d2, "year")) return true;
  return false;
};

export const isUnavailableDate = (
  n: DateTime,
  unavailableDates: CalendarContextProps["unavailableDates"],
  unavailable: CalendarContextProps["unavailable"],
  timeZone: string,
) => {
  const tmpSet = new Set();
  unavailableDates.forEach((date) => {
    tmpSet.add(toDateString(DateTime.fromISO(date, { zone: timeZone })));
  });
  unavailable.dates?.forEach((date) => {
    tmpSet.add(toDateString(DateTime.fromISO(date, { zone: timeZone })));
  });
  if (unavailable.daysOfWeek?.has(n.weekday)) {
    return true;
  }
  const dateISO = n.toISO();
  if (!dateISO) return;
  return tmpSet.has(
    toDateString(DateTime.fromISO(dateISO, { zone: timeZone })),
  );
};

export const isBeyondMinDate = (
  n: DateTime,
  minDate: CalendarContextProps["minDate"],
) => minDate && minDate.toMillis() > n.toMillis();

export const isBeyondMaxDate = (
  n: DateTime,
  maxDate: CalendarContextProps["maxDate"],
) => maxDate && maxDate.toMillis() < n.toMillis();

export const isValidDate = (
  n: DateTime | undefined,
  minDate = "",
  maxDate = "",
  unavailableDates: CalendarContextProps["unavailableDates"],
  unavailable: CalendarContextProps["unavailable"],
  timeZone: string,
) => {
  if (!n) return false;
  const normalizedMinDate = normalizeDate(minDate, timeZone);
  const normalizedMaxDate = normalizeDate(maxDate, timeZone);
  if (normalizedMinDate && isBeyondMinDate(n, normalizedMinDate)) return false;
  if (normalizedMaxDate && isBeyondMaxDate(n, normalizedMaxDate)) return false;
  return !isUnavailableDate(n, unavailableDates, unavailable, timeZone);
};

export const normalizeDate = (date: string, defaultTimeZone: string) => {
  return DateTime.fromISO(date, { zone: defaultTimeZone }).startOf("day");
};

export const toDateString = (date: DateTime<boolean>) =>
  date.startOf("day").toISO();

export function rangeUpdater(
  value: CalendarRangeContextProps["value"],
  newDate: DateTime,
  setValue: CalendarRangeContextProps["setValue"],
  onSelection: CalendarRangeContextProps["onSelection"],
) {
  if (value.start && isSameDay(value.start, newDate) && !value.end) {
    setValue((prev) => {
      return { ...prev, end: newDate };
    });
    onSelection?.({
      value: {
        start: toDateString(value.start) ?? undefined,
        end: toDateString(newDate) ?? undefined,
      },
    });
    return;
  }
  if (
    (!value.start && !value.end) ||
    (value.start && value.end) ||
    (value.start &&
      !value.end &&
      value.start.startOf("day").toMillis() > newDate.startOf("day").toMillis())
  ) {
    setValue({ start: newDate, end: null });
    onSelection?.({
      value: {
        start: toDateString(newDate) ?? undefined,
        end: undefined,
      },
    });
    return;
  }
  if (
    value.start &&
    !value.end &&
    value.start.startOf("day").toMillis() < newDate.startOf("day").toMillis()
  ) {
    setValue((prev) => {
      return { ...prev, end: newDate };
    });
    onSelection?.({
      value: {
        start: toDateString(value.start) ?? undefined,
        end: toDateString(newDate) ?? undefined,
      },
    });
    return;
  }
  if (!value.start && value.end) {
    setValue((prev) => {
      return { ...prev, start: newDate };
    });
    onSelection?.({
      value: {
        start: toDateString(newDate) ?? undefined,
        end: toDateString(value.end) ?? undefined,
      },
    });
    return;
  }
}
